import { ApiService } from "Services/ApiService";
import {
  CategoryAdminDTO,
  CategoryAdminRequestDTO,
} from "Types/Admin/AdminCategoryType";
import { ResultsDTO } from "Types/ResultsDTO";

export interface GetAdminCategoriesParams {
  page?: number;
  size?: number;
  search?: string;
}

interface AdminCategoriesResults {
  categories: CategoryAdminDTO[];
  totalCategories: number;
}

export interface AdminCategoriesListResult {
  categories: CategoryAdminDTO[];
  totalCategories: number;
}

export const CategoryAdminApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAllCategories: builder.query<
      CategoryAdminDTO[],
      GetAdminCategoriesParams | void
    >({
      query: (params) => ({
        url: "/admin/categories",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          size: params?.size ?? 1500,
          ...(params?.search ? { search: params.search } : {}),
        },
      }),
      transformResponse: (res: ResultsDTO<AdminCategoriesResults>) => {
        return (res.results?.categories || []).map((item) => ({
          ...item,
          image: item.imagePath || item.image || null,
        }));
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ categoryCode }) => ({
                type: "PrdCategory" as const,
                id: categoryCode,
              })),
              { type: "PrdCategory", id: "LIST" },
            ]
          : [{ type: "PrdCategory", id: "LIST" }],
    }),

    // Server-side paginated + searchable listing, used by the Admin Category listing page.
    getAdminCategoriesList: builder.query<
      AdminCategoriesListResult,
      { page: number; size: number; search?: string }
    >({
      query: ({ page, size, search }) => ({
        url: "/admin/categories",
        method: "GET",
        params: {
          page,
          size,
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (res: ResultsDTO<AdminCategoriesResults>) => ({
        categories: (res.results?.categories || []).map((item) => ({
          ...item,
          image: item.imagePath || item.image || null,
        })),
        totalCategories: res.results?.totalCategories ?? 0,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.categories.map(({ categoryCode }) => ({
                type: "PrdCategory" as const,
                id: categoryCode,
              })),
              { type: "PrdCategory", id: "LIST" },
            ]
          : [{ type: "PrdCategory", id: "LIST" }],
    }),

    getCategoryById: builder.query<CategoryAdminDTO, { id: number }>({
      query: ({ id }) => ({
        url: `/admin/categories/${id}`,
      }),
      transformResponse: (res: ResultsDTO<CategoryAdminDTO>) => {
        return {
          ...res.results,
          image: res.results.imagePath || res.results.image || null,
        };
      },
      providesTags: (result, error, { id }) => [{ type: "PrdCategory", id }],
    }),

    addCategory: builder.mutation<ResultsDTO<number>, { data: CategoryAdminRequestDTO }>({
      query: ({ data }) => ({
        url: "/admin/categories/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: () => [{ type: "PrdCategory", id: "LIST" }],
    }),

    updateCategory: builder.mutation<
      ResultsDTO<string>,
      { id: number; data: Partial<CategoryAdminRequestDTO> }
    >({
      query: ({ id, data }) => ({
        url: `/admin/categories/update/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrdCategory", id }],
    }),

    deleteCategory: builder.mutation<ResultsDTO, { id: number }>({
      query: ({ id }) => ({
        url: `/admin/categories/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrdCategory", id }],
    }),

    uploadCategoryImage: builder.mutation<ResultsDTO, { id: number; image: File }>({
      query: ({ id, image }) => {
        const formData = new FormData();
        formData.append("image", image);
        return {
          url: `/admin/categories/upload/${id}`,
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "PrdCategory", id },
        { type: "PrdCategory", id: "LIST" },
      ],
    }),

    deleteCategoryImage: builder.mutation<ResultsDTO, { id: number }>({
      query: ({ id }) => ({
        url: `/admin/categories/delete-image/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "PrdCategory", id },
        { type: "PrdCategory", id: "LIST" },
      ],
    }),

    bulkDeleteCategories: builder.mutation<ResultsDTO, { ids: number[] }>({
      query: ({ ids }) => ({
        url: `/admin/categories/bulk-delete`,
        method: "DELETE",
        params: { ids },
      }),
      invalidatesTags: [{ type: "PrdCategory", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAllCategoriesQuery,
  useGetAdminCategoriesListQuery,
  useLazyGetCategoryByIdQuery,
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useUploadCategoryImageMutation,
  useDeleteCategoryImageMutation,
  useBulkDeleteCategoriesMutation,
} = CategoryAdminApiSlice;
